import {uppercaseFirstLetter} from '../../../../components/utils';
import {toSpellDuration, toSpellRange} from '../../../../components/render-helpers';
import type {CharacterCatalogQuery, SpellDuration, SpellRange} from '../../../../generated/types';
import {Catalog, ClassData, FeatureData, SpellDice} from '../model/catalog';
import {normalizeChoose} from '../model/normalize-choose';
import type {FeatureProps} from '../model/types';
import {parseOrigin} from './parse-origin';

type ClassSource = CharacterCatalogQuery['classes'][number];
type FeatureSource = CharacterCatalogQuery['features'][number];

export function createCatalog(data: CharacterCatalogQuery): Catalog {
	return new Catalog({
		classes: data.classes.map(toClassData),
		species: data.species.map((specie) => ({
			id: specie.id,
			title: specie.title,
			speed: specie.speed,
			sizes: specie.sizes.map((size) => size.category),
		})),
		origins: data.origins.map(parseOrigin),
		feats: data.feats.map((feat) => ({
			id: feat.id,
			title: feat.title,
			category: feat.category,
			variantKey: feat.variantKey,
			repeatable: !!feat.repeatable,
		})),
		features: data.features.map(toFeatureData),
		spells: data.spellsWithClasses.map((spell) => ({
			id: spell.id,
			title: spell.title,
			level: spell.level,
			school: spell.school,
			classIds: (spell.classes || []).map((spellClass) => spellClass.id),
			castingTime: spell.time[0] && {unit: spell.time[0].unit, amount: spell.time[0].number},
			ritual: !!spell.ritual,
			concentration: spell.duration.some((duration) => duration.concentration),
			attackType: spell.attackType || undefined,
			damageTypes: spell.damage || [],
			savingThrows: spell.savingThrow || [],
			...splitSpellProps(spell.props),
			choose: normalizeChoose(spell.choose),
			rangeText: toSpellRange(spell.range as SpellRange) || '',
			durationText: toSpellDuration(spell.duration as SpellDuration[])
				.filter(Boolean)
				.join(' или '),
			components: {
				verbal: !!spell.components.v,
				somatic: !!spell.components.s,
				material: spell.components.m?.text || undefined,
			},
		})),
		items: data.items.map((item) => ({
			id: item.id,
			title: item.title,
			type: item.type,
			subTypes: item.subType || [],
			weaponCategory: item.weaponCategory,
			weaponMastery: item.weaponMastery || [],
		})),
		languages: data.languages,
	});
}

function splitSpellProps(source: unknown): {props?: FeatureProps; dice?: SpellDice} {
	if (!source || typeof source !== 'object') return {};
	const {dice, ...effects} = source as FeatureProps & {dice?: SpellDice};
	return {props: Object.keys(effects).length ? effects : undefined, dice};
}

function toClassData(source: ClassSource): ClassData {
	const {armorProficiency, weaponProficiency, toolsProficiency, multiclassRequirement} = source.props || {};
	const props: FeatureProps = {
		armorProficiencies: armorProficiency,
		weaponProficiencies: weaponProficiency,
		toolsProficiencies: toolsProficiency,
	};
	return {
		id: source.id,
		title: source.title,
		genitiveTitle: uppercaseFirstLetter(source.cases.genitive),
		hitDie: source.hitDie,
		saves: source.saves,
		props,
		choose: normalizeChoose(source.choose),
		table: {columns: source.columns, rows: (source.rows || []).map((row) => row || [])},
		subClasses: source.subClasses || [],
		multiclassRequirement,
		equipment: source.equipment || undefined,
	};
}

function toFeatureData(source: FeatureSource): FeatureData {
	return {
		id: source.id,
		title: source.title,
		description: source.description || '',
		levels: (source.levels || []).map(Number).filter((level) => level > 0),
		order: source.order || 0,
		type: source.type,
		props: source.props || undefined,
		choose: normalizeChoose(source.choose),
		classId: source.classId,
		subClassId: source.subClassId,
		specieId: source.specieId,
		featId: source.featId,
	};
}
